import { isObject, size, pick } from "lodash";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { notify, TYPE } from "@8x8/oxygen-toaster";

import { EVENT_TYPES, triggerEvent } from "@/events";
import Debug from "@/react/debug";

import {
  pbxIdSelector,
  setCustomer,
  setReactCustomer,
  setSkipNextSync,
} from "./customerSlice";
import enhanceWithVueAttributes from "./enhanceWithVueAttributes";
import clearCustomer from "./clearCustomer";
import commitPreselectedPbxId from "./commitPreselectedPbxId";
import commitPreselectedTenantId from "./commitPreselectedTenantId";
import { VUE_APP_NODE_ENV } from "./constants";

export const PILOT = "pilot";
export const PRODUCTION = "production";

const handleSuccess = (message) => {
  notify({
    type: TYPE.success,
    content: `Customer Sync: ${message}`,
    duration: 5000,
  });
};

const handleError = (message) => {
  notify({
    type: TYPE.error,
    content: `Customer Sync: ${message}`,
    duration: 5000,
  });
};

const triggerSyncWithVue = (backwardsCompatibleCustomerData) => {
  const payload = pick(backwardsCompatibleCustomerData, [
    "customerId",
    "customerName",
    "pbx",
    "pbxList",
    "adminList",
    "tenantInfo",
  ]);
  Debug.log(
    "triggerSyncWithVue->VUE_APP_NODE_ENV:",
    JSON.stringify({ VUE_APP_NODE_ENV })
  );

  // This will be used in requests to the old BE - so it will have to
  // specify the currently active platform. For all intents and purposes,
  // everything not in production, is in pilot!
  payload.platform = VUE_APP_NODE_ENV === PRODUCTION ? PRODUCTION : PILOT;
  payload.success = handleSuccess;
  payload.error = handleError;

  // This event triggers a call to fetchCustomer in the same way that Vue
  // internally does in findAndFetchCustomer. The Vue code will then perform
  // an additional network request to get the token, since that's how Vue
  // was designed to operate.
  console.debug(
    `[React Sync Flow] Dispatching ${EVENT_TYPES.REACT_INITIATED_CUSTOMER_UPDATE}.`,
    payload
  );
  triggerEvent(EVENT_TYPES.REACT_INITIATED_CUSTOMER_UPDATE, payload);
};

const applyReactCustomer = createAsyncThunk(
  "customer/applyReactCustomer",
  async (customerData, { getState, dispatch }) => {
    const isNonEmpty =
      customerData != null && isObject(customerData) && size(customerData) > 0;

    if (isNonEmpty) {
      try {
        const backwardsCompatibleCustomerData =
          enhanceWithVueAttributes(customerData);

        commitPreselectedPbxId(customerData, { dispatch });
        commitPreselectedTenantId(customerData, { dispatch });

        dispatch(setReactCustomer(customerData));
        dispatch(setCustomer(backwardsCompatibleCustomerData));

        // Fallback for 'pbx': if there was no preselected PBX to be
        // commited, then instead of leaving the Vue 'pbx' as undefined,
        // try to set to the currently set PBX.
        if (!backwardsCompatibleCustomerData?.pbx) {
          const state = getState();
          const pbxId = pbxIdSelector(state);
          console.debug(
            "[applyReactCustomer] No 'pbx' set on Vue customer yet. Falling back on existing pbxId.",
            pbxId
          );
          backwardsCompatibleCustomerData.pbx =
            backwardsCompatibleCustomerData?.pbxList.find(
              (pbx) => pbx.id === pbxId
            );
        }

        triggerSyncWithVue(backwardsCompatibleCustomerData);

        // See why this is needed in {@see customerSlice.js}.
        dispatch(setSkipNextSync(true));
      } catch (e) {
        if (e instanceof Error) {
          console.error(e);
          handleError(e?.message ?? "No message could be extracted.");
        }
      }
    } else {
      dispatch(clearCustomer());
      console.debug(
        `[React Sync Flow] Zero keys in backwardsCompatibleCustomerData. Signaling to clear Vuex state.`,
        { customerData }
      );
      triggerEvent(EVENT_TYPES.REACT_INITIATED_CUSTOMER_CLEAR);
    }
  }
);

export default applyReactCustomer;
